import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Archive,
  CalendarDays,
  Clock3,
  Edit3,
  LogOut,
  Mail,
  Phone,
  Plane,
  Plus,
  Search,
  Sparkles,
  Thermometer,
  Trash2,
  UserPlus,
  UserRound,
  UsersRound,
} from 'lucide-react';

const API = '/api';
const emptyCustomerForm = { name: '', phone: '', email: '', notes: '', preferred_service_id: '' };
const emptyBookingForm = { staff_customer_id: '', service_id: '', date: '', time: '', notes: '' };

const formatDate = (value) => {
  if (!value) return '–';
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' }).format(new Date(`${value}T12:00:00`));
};

const formatPrice = (price) => Number(price) > 0
  ? `${Number(price).toFixed(2).replace('.', ',')} €`
  : 'Preis auf Anfrage';

const StaffDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [activeArea, setActiveArea] = useState('appointments');
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [services, setServices] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingBooking, setSavingBooking] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [savingAbsence, setSavingAbsence] = useState(false);
  const [editingCustomerId, setEditingCustomerId] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [bookingFilter, setBookingFilter] = useState('upcoming');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [bookingForm, setBookingForm] = useState(emptyBookingForm);
  const [customerForm, setCustomerForm] = useState(emptyCustomerForm);
  const [absenceForm, setAbsenceForm] = useState({
    start_date: '',
    end_date: '',
    absence_type: 'Urlaub',
    reason: '',
  });

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [bookingsResponse, customersResponse, absencesResponse, servicesResponse, profileResponse] = await Promise.all([
        axios.get(`${API}/staff/bookings`, { headers }),
        axios.get(`${API}/staff/customers`, { headers }),
        axios.get(`${API}/staff/absences`, { headers }),
        axios.get(`${API}/staff/services`, { headers }),
        axios.get(`${API}/staff/profile`, { headers }),
      ]);
      setBookings(bookingsResponse.data);
      setCustomers(customersResponse.data);
      setAbsences(absencesResponse.data);
      setServices(servicesResponse.data.filter((service) => !service.coming_soon));
      setProfile(profileResponse.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Der Mitarbeiterbereich konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    if (!bookingForm.date || !profile?.id) {
      setAvailableSlots([]);
      return;
    }
    let active = true;
    setSlotsLoading(true);
    axios.get(`${API}/bookings/slots`, {
      params: { date: bookingForm.date, stylist_id: profile.id },
    }).then((response) => {
      if (active) {
        setAvailableSlots(response.data.slots || []);
        if (bookingForm.time && !(response.data.slots || []).includes(bookingForm.time)) {
          setBookingForm((current) => ({ ...current, time: '' }));
        }
      }
    }).catch(() => {
      if (active) toast.error('Freie Uhrzeiten konnten nicht geladen werden.');
    }).finally(() => {
      if (active) setSlotsLoading(false);
    });
    return () => { active = false; };
  }, [bookingForm.date, profile?.id]);

  const today = new Date().toISOString().slice(0, 10);
  const notificationEmailActive = Boolean(profile?.email && !profile.email.toLowerCase().endsWith('@salon.de'));

  const upcomingBookings = useMemo(
    () => bookings.filter((booking) => booking.status === 'confirmed' && booking.date >= today),
    [bookings, today],
  );

  const visibleBookings = useMemo(() => {
    if (bookingFilter === 'upcoming') {
      return [...upcomingBookings].sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`));
    }
    return bookings;
  }, [bookingFilter, bookings, upcomingBookings]);

  const filteredCustomers = useMemo(() => {
    const term = customerSearch.trim().toLowerCase();
    if (!term) return customers;
    return customers.filter((customer) =>
      [customer.name, customer.phone, customer.email, customer.last_service_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term)),
    );
  }, [customers, customerSearch]);

  const selectedCustomer = customers.find((customer) => customer.id === bookingForm.staff_customer_id);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const chooseCustomerForBooking = (customer) => {
    setBookingForm((current) => ({
      ...current,
      staff_customer_id: customer.id,
      service_id: customer.preferred_service_id || customer.last_service_id || current.service_id,
      time: '',
    }));
  };

  const openBookingForCustomer = (customer) => {
    chooseCustomerForBooking(customer);
    setActiveArea('appointments');
    window.scrollTo({ top: 250, behavior: 'smooth' });
  };

  const createBooking = async (event) => {
    event.preventDefault();
    if (!bookingForm.staff_customer_id || !bookingForm.service_id || !bookingForm.date || !bookingForm.time) {
      toast.error('Bitte Kunde, Dienstleistung, Datum und Uhrzeit auswählen.');
      return;
    }
    setSavingBooking(true);
    try {
      await axios.post(`${API}/staff/bookings`, bookingForm, { headers: getAuthHeaders() });
      const hasCustomerEmail = Boolean(selectedCustomer?.email);
      toast.success(
        hasCustomerEmail
          ? 'Termin gespeichert. Kunde, Salon und Melissa wurden per E-Mail informiert.'
          : 'Termin gespeichert. Melissa und der Salon wurden informiert; der Kunde hat keine E-Mail-Adresse.',
      );
      setBookingForm(emptyBookingForm);
      setAvailableSlots([]);
      await loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Termin konnte nicht angelegt werden.');
    } finally {
      setSavingBooking(false);
    }
  };

  const removeBooking = async (booking) => {
    if (!window.confirm(`Termin von ${booking.user_name} am ${formatDate(booking.date)} wirklich stornieren?`)) return;
    try {
      await axios.delete(`${API}/staff/bookings/${booking.id}`, { headers: getAuthHeaders() });
      toast.success(booking.user_email ? 'Termin storniert. Der Kunde wurde per E-Mail informiert.' : 'Termin storniert.');
      await loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Termin konnte nicht storniert werden.');
    }
  };

  const saveCustomer = async (event) => {
    event.preventDefault();
    if (!customerForm.name.trim()) {
      toast.error('Bitte den Namen eintragen.');
      return;
    }
    if (!customerForm.phone.trim() && !customerForm.email.trim()) {
      toast.error('Bitte Telefonnummer oder E-Mail-Adresse eintragen.');
      return;
    }
    const payload = {
      ...customerForm,
      email: customerForm.email.trim() || null,
      phone: customerForm.phone.trim() || null,
      notes: customerForm.notes.trim() || null,
      preferred_service_id: customerForm.preferred_service_id || null,
    };
    setSavingCustomer(true);
    try {
      let response;
      if (editingCustomerId) {
        response = await axios.put(`${API}/staff/customers/${editingCustomerId}`, payload, { headers: getAuthHeaders() });
        toast.success('Kundendaten aktualisiert.');
      } else {
        response = await axios.post(`${API}/staff/customers`, payload, { headers: getAuthHeaders() });
        toast.success('Kunde gespeichert – jetzt kann direkt ein Termin angelegt werden.');
      }
      const savedId = response.data.id;
      setCustomerForm(emptyCustomerForm);
      setEditingCustomerId(null);
      await loadDashboard();
      if (!editingCustomerId && savedId) {
        setBookingForm((current) => ({ ...current, staff_customer_id: savedId, service_id: payload.preferred_service_id || '' }));
        setActiveArea('appointments');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde konnte nicht gespeichert werden.');
    } finally {
      setSavingCustomer(false);
    }
  };

  const editCustomer = (customer) => {
    setEditingCustomerId(customer.id);
    setCustomerForm({
      name: customer.name || '',
      phone: customer.phone || '',
      email: customer.email || '',
      notes: customer.notes || '',
      preferred_service_id: customer.preferred_service_id || customer.last_service_id || '',
    });
    setActiveArea('customers');
    window.scrollTo({ top: 250, behavior: 'smooth' });
  };

  const cancelCustomerEdit = () => {
    setEditingCustomerId(null);
    setCustomerForm(emptyCustomerForm);
  };

  const archiveCustomer = async (customer) => {
    if (!window.confirm(`${customer.name} aus der aktiven Kundenkartei entfernen? Die Termin-Historie bleibt erhalten.`)) return;
    try {
      await axios.delete(`${API}/staff/customers/${customer.id}`, { headers: getAuthHeaders() });
      if (bookingForm.staff_customer_id === customer.id) setBookingForm(emptyBookingForm);
      toast.success('Kunde archiviert. Frühere Termine bleiben erhalten.');
      await loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde konnte nicht archiviert werden.');
    }
  };

  const createAbsence = async (event) => {
    event.preventDefault();
    const payload = { ...absenceForm, end_date: absenceForm.end_date || absenceForm.start_date };
    if (!payload.start_date) {
      toast.error('Bitte den ersten Tag auswählen.');
      return;
    }
    setSavingAbsence(true);
    try {
      await axios.post(`${API}/staff/absences`, payload, { headers: getAuthHeaders() });
      toast.success(`${payload.absence_type} wurde im Kalender gesperrt.`);
      setAbsenceForm({ start_date: '', end_date: '', absence_type: 'Urlaub', reason: '' });
      await loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Abwesenheit konnte nicht gespeichert werden.');
    } finally {
      setSavingAbsence(false);
    }
  };

  const removeAbsence = async (absence) => {
    if (!window.confirm(`${absence.absence_type} vom ${formatDate(absence.start_date)} wirklich entfernen?`)) return;
    try {
      await axios.delete(`${API}/staff/absences/${absence.id}`, { headers: getAuthHeaders() });
      toast.success('Abwesenheit entfernt.');
      await loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Abwesenheit konnte nicht entfernt werden.');
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-600">Melissa-Bereich wird geladen…</div>;
  }

  const areaButton = (key, label, Icon) => (
    <Button
      type="button"
      variant={activeArea === key ? 'default' : 'outline'}
      onClick={() => setActiveArea(key)}
      className={activeArea === key ? 'bg-blue-600 hover:bg-blue-700' : 'bg-white'}
    >
      <Icon className="w-4 h-4 mr-2" /> {label}
    </Button>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-950 text-white">
        <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-sm text-blue-300">Salon Galo · Smarter Mitarbeiterbereich</p>
            <h1 className="text-2xl font-semibold">Hallo {user?.name || 'Melissa'}</h1>
            <p className="text-sm text-slate-300 mt-1">{profile?.email}</p>
          </div>
          <Button type="button" variant="outline" onClick={handleLogout} className="border-slate-600 bg-transparent text-white hover:bg-slate-800 hover:text-white">
            <LogOut className="w-4 h-4 mr-2" /> Abmelden
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 space-y-6">
        <section className="grid gap-4 sm:grid-cols-3">
          <Card className="p-5"><CalendarDays className="w-6 h-6 text-blue-600 mb-3" /><p className="text-sm text-slate-500">Kommende Termine</p><p className="text-3xl font-semibold">{upcomingBookings.length}</p></Card>
          <Card className="p-5"><UsersRound className="w-6 h-6 text-emerald-600 mb-3" /><p className="text-sm text-slate-500">Eigene Kunden</p><p className="text-3xl font-semibold">{customers.length}</p></Card>
          <Card className="p-5"><Mail className="w-6 h-6 text-violet-600 mb-3" /><p className="text-sm text-slate-500">Mitarbeiter-E-Mails</p><p className={notificationEmailActive ? 'font-semibold text-emerald-700' : 'font-semibold text-amber-700'}>{notificationEmailActive ? 'Aktiv' : 'Echte E-Mail fehlt'}</p><p className="text-xs text-slate-500 mt-1">{profile?.email}</p></Card>
        </section>

        <nav className="flex flex-wrap gap-2" aria-label="Mitarbeiterbereiche">
          {areaButton('appointments', 'Termine', CalendarDays)}
          {areaButton('customers', 'Kundenkartei', UsersRound)}
          {areaButton('absences', 'Urlaub & Krankheit', Plane)}
        </nav>

        {activeArea === 'appointments' && (
          <>
            <section className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
              <Card className="p-5 sm:p-6">
                <h2 className="text-xl font-semibold mb-1 flex items-center gap-2"><Plus className="w-5 h-5 text-blue-600" /> Kundentermin eintragen</h2>
                <p className="text-sm text-slate-500 mb-5">E-Mail ist nicht erforderlich. Wiederkehrende Services werden automatisch vorgeschlagen.</p>
                <form onSubmit={createBooking} className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between gap-3"><Label htmlFor="staff-customer">Kunde *</Label><button type="button" className="text-sm text-blue-600 hover:underline" onClick={() => setActiveArea('customers')}>+ Neuer Kunde</button></div>
                    <select id="staff-customer" className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={bookingForm.staff_customer_id} onChange={(event) => {
                      const customer = customers.find((item) => item.id === event.target.value);
                      if (customer) chooseCustomerForBooking(customer);
                      else setBookingForm((current) => ({ ...current, staff_customer_id: '', service_id: '', time: '' }));
                    }}>
                      <option value="">Kunde auswählen</option>
                      {customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.name}{customer.phone ? ` · ${customer.phone}` : ''}</option>)}
                    </select>
                    {selectedCustomer && (
                      <div className="mt-2 rounded-lg bg-blue-50 p-3 text-sm text-blue-950">
                        <p className="font-medium">{selectedCustomer.name}</p>
                        <p>{selectedCustomer.phone || 'Keine Telefonnummer'}{selectedCustomer.email ? ` · ${selectedCustomer.email}` : ' · Keine E-Mail'}</p>
                        {(selectedCustomer.last_service_name || selectedCustomer.preferred_service_id) && <p className="mt-1 flex items-center gap-1 text-blue-700"><Sparkles className="w-3.5 h-3.5" /> Letzter Service: {selectedCustomer.last_service_name || 'gespeicherter Lieblingsservice'}</p>}
                      </div>
                    )}
                  </div>
                  <div><Label htmlFor="staff-service">Dienstleistung *</Label><select id="staff-service" className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={bookingForm.service_id} onChange={(event) => setBookingForm({ ...bookingForm, service_id: event.target.value })}><option value="">Bitte auswählen</option>{services.map((service) => <option key={service.id} value={service.id}>{service.name} · {service.price_is_from ? 'ab ' : ''}{formatPrice(service.price)}</option>)}</select></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label htmlFor="staff-date">Datum *</Label><Input id="staff-date" type="date" min={today} className="mt-1" value={bookingForm.date} onChange={(event) => setBookingForm({ ...bookingForm, date: event.target.value, time: '' })} /></div>
                    <div><Label htmlFor="staff-time">Freie Uhrzeit *</Label><select id="staff-time" disabled={!bookingForm.date || slotsLoading} className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-60" value={bookingForm.time} onChange={(event) => setBookingForm({ ...bookingForm, time: event.target.value })}><option value="">{slotsLoading ? 'Lädt…' : availableSlots.length ? 'Bitte wählen' : 'Keine Zeit frei'}</option>{availableSlots.map((slot) => <option key={slot} value={slot}>{slot} Uhr</option>)}</select></div>
                  </div>
                  <div><Label htmlFor="staff-notes">Terminnotiz</Label><Textarea id="staff-notes" className="mt-1" placeholder="Optional, z. B. Farbton oder Wunsch" value={bookingForm.notes} onChange={(event) => setBookingForm({ ...bookingForm, notes: event.target.value })} /></div>
                  <Button type="submit" disabled={savingBooking} className="w-full bg-blue-600 hover:bg-blue-700">{savingBooking ? 'Wird gespeichert…' : 'Termin verbindlich eintragen'}</Button>
                </form>
              </Card>

              <Card className="overflow-hidden">
                <div className="p-5 sm:p-6 border-b flex flex-wrap items-center justify-between gap-3">
                  <div><h2 className="text-xl font-semibold">Meine Kundentermine</h2><p className="text-sm text-slate-500">Nur Termine, die Melissa zugeordnet sind.</p></div>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant={bookingFilter === 'upcoming' ? 'default' : 'outline'} onClick={() => setBookingFilter('upcoming')}>Kommend</Button>
                    <Button type="button" size="sm" variant={bookingFilter === 'all' ? 'default' : 'outline'} onClick={() => setBookingFilter('all')}>Alle</Button>
                  </div>
                </div>
                <div className="divide-y max-h-[720px] overflow-y-auto">
                  {visibleBookings.length === 0 && <p className="p-6 text-slate-500">Keine Termine in dieser Ansicht.</p>}
                  {visibleBookings.map((booking) => (
                    <div key={booking.id} className="p-4 sm:p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-semibold">{booking.user_name}</p>
                          <p className="text-sm text-slate-500">{booking.user_phone || booking.user_email || 'Keine Kontaktdaten'}</p>
                        </div>
                        <span className={booking.status === 'confirmed' ? 'rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700' : 'rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-500'}>{booking.status === 'confirmed' ? 'Bestätigt' : 'Storniert'}</span>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                        <div><p className="text-xs text-slate-500">Termin</p><p className="font-medium">{formatDate(booking.date)}</p><p className="flex items-center gap-1"><Clock3 className="w-3.5 h-3.5" /> {booking.time} Uhr</p></div>
                        <div><p className="text-xs text-slate-500">Dienstleistung</p><p className="font-medium">{booking.service_name}</p>{booking.notes && <p className="text-xs text-slate-500 mt-1">{booking.notes}</p>}</div>
                      </div>
                      {booking.status === 'confirmed' && <Button type="button" variant="outline" size="sm" onClick={() => removeBooking(booking)} className="mt-4 text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4 mr-2" /> Termin stornieren</Button>}
                    </div>
                  ))}
                </div>
              </Card>
            </section>
          </>
        )}

        {activeArea === 'customers' && (
          <section className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr]">
            <Card className="p-5 sm:p-6 h-fit">
              <h2 className="text-xl font-semibold mb-1 flex items-center gap-2"><UserPlus className="w-5 h-5 text-blue-600" /> {editingCustomerId ? 'Kundendaten bearbeiten' : 'Neuen Kunden anlegen'}</h2>
              <p className="text-sm text-slate-500 mb-5">Eine Telefonnummer reicht aus. Die E-Mail-Adresse bleibt freiwillig.</p>
              <form onSubmit={saveCustomer} className="space-y-4">
                <div><Label htmlFor="customer-name">Name *</Label><Input id="customer-name" className="mt-1" autoComplete="name" value={customerForm.name} onChange={(event) => setCustomerForm({ ...customerForm, name: event.target.value })} /></div>
                <div><Label htmlFor="customer-phone">Telefon</Label><Input id="customer-phone" type="tel" className="mt-1" autoComplete="tel" placeholder="z. B. 0163 6849710" value={customerForm.phone} onChange={(event) => setCustomerForm({ ...customerForm, phone: event.target.value })} /></div>
                <div><Label htmlFor="customer-email">E-Mail (optional)</Label><Input id="customer-email" type="email" className="mt-1" autoComplete="email" value={customerForm.email} onChange={(event) => setCustomerForm({ ...customerForm, email: event.target.value })} /></div>
                <div><Label htmlFor="customer-service">Bevorzugter Service</Label><select id="customer-service" className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={customerForm.preferred_service_id} onChange={(event) => setCustomerForm({ ...customerForm, preferred_service_id: event.target.value })}><option value="">Noch nicht festlegen</option>{services.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}</select></div>
                <div><Label htmlFor="customer-notes">Kundennotiz</Label><Textarea id="customer-notes" className="mt-1" placeholder="z. B. Rezeptur, Haarwunsch oder Besonderheiten" value={customerForm.notes} onChange={(event) => setCustomerForm({ ...customerForm, notes: event.target.value })} /></div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={savingCustomer} className="flex-1 bg-blue-600 hover:bg-blue-700">{savingCustomer ? 'Wird gespeichert…' : editingCustomerId ? 'Änderungen speichern' : 'Kunde speichern'}</Button>
                  {editingCustomerId && <Button type="button" variant="outline" onClick={cancelCustomerEdit}>Abbrechen</Button>}
                </div>
              </form>
            </Card>

            <Card className="overflow-hidden">
              <div className="p-5 sm:p-6 border-b">
                <h2 className="text-xl font-semibold">Meine Kundenkartei</h2>
                <p className="text-sm text-slate-500 mb-4">Service-Verlauf und nächster Termin auf einen Blick.</p>
                <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input aria-label="Kunden suchen" className="pl-9" placeholder="Name, Telefon, E-Mail oder Service suchen" value={customerSearch} onChange={(event) => setCustomerSearch(event.target.value)} /></div>
              </div>
              <div className="divide-y max-h-[760px] overflow-y-auto">
                {filteredCustomers.length === 0 && <p className="p-6 text-slate-500">Kein Kunde gefunden.</p>}
                {filteredCustomers.map((customer) => (
                  <div key={customer.id} className="p-4 sm:p-5">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2"><h3 className="font-semibold text-lg">{customer.name}</h3><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{customer.bookings_count || 0} Termin{customer.bookings_count === 1 ? '' : 'e'}</span></div>
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                          {customer.phone && <a className="flex items-center gap-1 hover:text-blue-600" href={`tel:${customer.phone}`}><Phone className="w-3.5 h-3.5" /> {customer.phone}</a>}
                          {customer.email && <a className="flex items-center gap-1 hover:text-blue-600" href={`mailto:${customer.email}`}><Mail className="w-3.5 h-3.5" /> {customer.email}</a>}
                          {!customer.email && <span className="text-amber-700">Keine E-Mail</span>}
                        </div>
                        {customer.last_service_name && <p className="mt-2 text-sm"><span className="text-slate-500">Letzter Service:</span> <strong>{customer.last_service_name}</strong></p>}
                        {customer.next_appointment && <p className="mt-1 text-sm text-emerald-700"><span className="font-medium">Nächster Termin:</span> {formatDate(customer.next_appointment.date)}, {customer.next_appointment.time} Uhr · {customer.next_appointment.service_name}</p>}
                        {customer.notes && <p className="mt-2 rounded-md bg-amber-50 p-2 text-sm text-amber-950">{customer.notes}</p>}
                      </div>
                      <div className="flex flex-wrap gap-2 shrink-0">
                        <Button type="button" size="sm" onClick={() => openBookingForCustomer(customer)} className="bg-blue-600 hover:bg-blue-700"><CalendarDays className="w-4 h-4 mr-1" /> Termin</Button>
                        <Button type="button" size="icon" variant="outline" aria-label="Kunde bearbeiten" onClick={() => editCustomer(customer)}><Edit3 className="w-4 h-4" /></Button>
                        <Button type="button" size="icon" variant="outline" aria-label="Kunde archivieren" onClick={() => archiveCustomer(customer)} className="text-red-600"><Archive className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </section>
        )}

        {activeArea === 'absences' && (
          <section className="grid gap-6 lg:grid-cols-2">
            <Card className="p-5 sm:p-6">
              <h2 className="text-xl font-semibold mb-1 flex items-center gap-2"><Plane className="w-5 h-5 text-blue-600" /> Urlaub oder Krankheit</h2>
              <p className="text-sm text-slate-500 mb-5">Die ausgewählten Tage sind nur bei Melissa nicht mehr buchbar.</p>
              <form onSubmit={createAbsence} className="space-y-4">
                <div><Label htmlFor="absence-type">Art *</Label><select id="absence-type" className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={absenceForm.absence_type} onChange={(event) => setAbsenceForm({ ...absenceForm, absence_type: event.target.value })}><option>Urlaub</option><option>Krankheit</option></select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label htmlFor="absence-start">Von *</Label><Input id="absence-start" type="date" min={today} className="mt-1" value={absenceForm.start_date} onChange={(event) => setAbsenceForm({ ...absenceForm, start_date: event.target.value })} /></div>
                  <div><Label htmlFor="absence-end">Bis</Label><Input id="absence-end" type="date" min={absenceForm.start_date || today} className="mt-1" value={absenceForm.end_date} onChange={(event) => setAbsenceForm({ ...absenceForm, end_date: event.target.value })} /></div>
                </div>
                <div><Label htmlFor="absence-reason">Notiz</Label><Input id="absence-reason" className="mt-1" placeholder="Optional" value={absenceForm.reason} onChange={(event) => setAbsenceForm({ ...absenceForm, reason: event.target.value })} /></div>
                <Button type="submit" disabled={savingAbsence} className="w-full bg-slate-900 hover:bg-slate-800">{savingAbsence ? 'Wird gespeichert…' : 'Tage sperren'}</Button>
              </form>
            </Card>
            <Card className="p-5 sm:p-6">
              <h2 className="text-xl font-semibold mb-4">Eingetragene Abwesenheiten</h2>
              <div className="space-y-2">
                {absences.length === 0 && <p className="text-sm text-slate-500">Keine Abwesenheiten eingetragen.</p>}
                {absences.map((absence) => (
                  <div key={absence.id} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 border p-3">
                    <div className="flex gap-3">{absence.absence_type === 'Krankheit' ? <Thermometer className="w-5 h-5 text-red-500" /> : <Plane className="w-5 h-5 text-blue-500" />}<div><p className="font-medium">{absence.absence_type}</p><p className="text-sm text-slate-600">{formatDate(absence.start_date)}{absence.end_date !== absence.start_date ? ` – ${formatDate(absence.end_date)}` : ''}</p>{absence.reason && <p className="text-xs text-slate-500">{absence.reason}</p>}</div></div>
                    <Button type="button" variant="ghost" size="icon" aria-label="Abwesenheit entfernen" onClick={() => removeAbsence(absence)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                  </div>
                ))}
              </div>
            </Card>
          </section>
        )}
      </main>
    </div>
  );
};

export default StaffDashboard;
